import {ReloadOutlined, SaveOutlined, SyncOutlined} from '@ant-design/icons';
import {BeLinkCOS} from '@be-link/cos';
import {
	Affix,
	Alert,
	Button,
	Card,
	Descriptions,
	Empty,
	message,
	Space,
	Spin,
	Switch,
	Table,
	Tabs,
	Tag,
	Typography,
} from 'antd';
import type {ColumnsType} from 'antd/es/table';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {type CosUploader, uploadTextToCosPath} from '../utils/cosUpload';
import {formatYmdHmsZh} from '../utils/formatDateTime';

const { Paragraph, Text } = Typography;

type AppConfigEnv = 'test' | 'production';
type ShowHotUpdateBtnPlatform = 'ios' | 'android' | 'harmony';

type AppConfigEnvOption = {
	label: string;
	value: AppConfigEnv;
	url: string;
	tagColor: string;
};

type RemoteConfig = Record<string, unknown> & {
	showHotUpdateBtn?: ShowHotUpdateBtnManifest;
};

type ShowHotUpdateBtnManifest = {
	ios?: Record<string, boolean>;
	android?: Record<string, boolean>;
	harmony?: Record<string, boolean>;
};

type EditablePlatformRow = {
	id: string;
	version: string;
	enabled: boolean;
};

type EditableShowHotUpdateBtn = Record<
	ShowHotUpdateBtnPlatform,
	EditablePlatformRow[]
>;

type EnvState = {
	loading: boolean;
	saving: boolean;
	loaded: boolean;
	lastModified: string;
	fullConfig: RemoteConfig | null;
	showHotUpdateBtn: EditableShowHotUpdateBtn;
	lastSavedShowHotUpdateBtn: string;
};

type BaseVersionState = {
	loading: boolean;
	error: string;
};

type PushyNativePackage = {
	name?: string;
	status?: string;
};

type PushyPackageListResponse = {
	data?: PushyNativePackage[];
};

const APP_CONFIG_ENV_OPTIONS: AppConfigEnvOption[] = [
	{
		label: '测试环境',
		value: 'test',
		url: 'https://cdn.8848top.com/pandora-miniprogram/app-update/dev/config.json',
		tagColor: 'gold',
	},
	{
		label: '生产环境',
		value: 'production',
		url: 'https://cdn.8848top.com/pandora-miniprogram/app-update/prod/config.json',
		tagColor: 'red',
	},
];

const PUSHY_PLATFORMS: readonly ShowHotUpdateBtnPlatform[] = [
	'ios',
	'android',
	'harmony',
] as const;

const PLATFORM_META: Record<
	ShowHotUpdateBtnPlatform,
	{ label: string; color: string }
> = {
	ios: {
		label: 'iOS',
		color: 'blue',
	},
	android: {
		label: 'Android',
		color: 'green',
	},
	harmony: {
		label: '鸿蒙',
		color: 'volcano',
	},
};

const PUSHY_PACKAGE_APP_IDS: Record<
	AppConfigEnv,
	Record<ShowHotUpdateBtnPlatform, number>
> = {
	test: {
		ios: 32263,
		android: 32264,
		harmony: 32328,
	},
	production: {
		ios: 31976,
		android: 31975,
		harmony: 32320,
	},
};

const PUSHY_API_TOKEN =
	'pushy_m_s-T5rtcIZlW_HSfSGGcQFuEGY-iQ4FdFF5cyaUXqVj_jce';
const PUSHY_API_BASE_URL = 'https://update.reactnative.cn/api';
const PUSHY_PACKAGE_LIST_LIMIT = 1000;

function createRowId() {
	return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function createEmptyShowHotUpdateBtn(): EditableShowHotUpdateBtn {
	return {
		ios: [],
		android: [],
		harmony: [],
	};
}

function createEmptyEnvState(): EnvState {
	const showHotUpdateBtn = createEmptyShowHotUpdateBtn();
	return {
		loading: false,
		saving: false,
		loaded: false,
		lastModified: '',
		fullConfig: null,
		showHotUpdateBtn,
		lastSavedShowHotUpdateBtn: JSON.stringify(showHotUpdateBtn),
	};
}

function createEmptyBaseVersionStates(): Record<
	AppConfigEnv,
	Record<ShowHotUpdateBtnPlatform, BaseVersionState>
> {
	const createPlatformStates = () => ({
		ios: { loading: false, error: '' },
		android: { loading: false, error: '' },
		harmony: { loading: false, error: '' },
	});

	return {
		test: createPlatformStates(),
		production: createPlatformStates(),
	};
}

function getErrorMessage(error: unknown) {
	if (error instanceof Error) {
		return error.message;
	}

	return '未知错误';
}

function coerceRemoteBoolean(value: unknown, fallback = false) {
	if (value == null) {
		return fallback;
	}
	if (value === true || value === 1) {
		return true;
	}
	if (value === false || value === 0) {
		return false;
	}
	if (typeof value === 'string') {
		const normalized = value.trim().toLowerCase();
		if (['true', '1', 'yes'].includes(normalized)) {
			return true;
		}
		if (['false', '0', 'no'].includes(normalized)) {
			return false;
		}
	}

	return fallback;
}

function normalizeString(value: unknown) {
	return typeof value === 'string' ? value.trim() : '';
}

function normalizeBaseVersions(
	packages: PushyNativePackage[] | undefined,
): string[] {
	const seen = new Set<string>();
	const versions: string[] = [];

	for (const pkg of packages || []) {
		const version = normalizeString(pkg.name);
		if (!version || seen.has(version)) {
			continue;
		}
		seen.add(version);
		versions.push(version);
	}

	return versions;
}

function normalizeShowHotUpdateBtn(
	config: RemoteConfig | null,
): EditableShowHotUpdateBtn {
	const manifest = config?.showHotUpdateBtn;
	const result = createEmptyShowHotUpdateBtn();

	for (const platform of PUSHY_PLATFORMS) {
		const platformConfig = manifest?.[platform];
		if (!platformConfig || typeof platformConfig !== 'object') {
			result[platform] = [];
			continue;
		}

		result[platform] = Object.entries(platformConfig)
			.map(([version, enabled]) => ({
				id: createRowId(),
				version: normalizeString(version),
				enabled: coerceRemoteBoolean(enabled, false),
			}))
			.filter((row) => Boolean(row.version));
	}

	return result;
}

function buildShowHotUpdateBtnForSave(
	config: EditableShowHotUpdateBtn,
): ShowHotUpdateBtnManifest {
	return {
		ios: Object.fromEntries(
			config.ios
				.map((row) => [row.version.trim(), row.enabled] as const)
				.filter(([version]) => Boolean(version)),
		),
		android: Object.fromEntries(
			config.android
				.map((row) => [row.version.trim(), row.enabled] as const)
				.filter(([version]) => Boolean(version)),
		),
		harmony: Object.fromEntries(
			config.harmony
				.map((row) => [row.version.trim(), row.enabled] as const)
				.filter(([version]) => Boolean(version)),
		),
	};
}

function serializeShowHotUpdateBtn(config: EditableShowHotUpdateBtn) {
	return JSON.stringify(buildShowHotUpdateBtnForSave(config));
}

function formatConfigForUpload(config: RemoteConfig) {
	return `${JSON.stringify(config, null, 4)}\n`;
}

function validateShowHotUpdateBtn(
	config: EditableShowHotUpdateBtn,
): string | null {
	for (const platform of PUSHY_PLATFORMS) {
		const seen = new Set<string>();
		for (const [index, row] of config[platform].entries()) {
			const version = row.version.trim();
			const prefix = `${PLATFORM_META[platform].label} 第 ${index + 1} 条`;
			if (!version) {
				return `${prefix} 需要填写版本号`;
			}
			if (seen.has(version)) {
				return `${PLATFORM_META[platform].label} 存在重复版本号：${version}`;
			}
			seen.add(version);
		}
	}

	return null;
}

function updateRow(
	rows: EditablePlatformRow[],
	rowId: string,
	updater: (row: EditablePlatformRow) => EditablePlatformRow,
) {
	return rows.map((row) => (row.id === rowId ? updater(row) : row));
}

async function fetchPushyBaseVersions(
	env: AppConfigEnv,
	platform: ShowHotUpdateBtnPlatform,
) {
	const appId = PUSHY_PACKAGE_APP_IDS[env][platform];
	const url = `${PUSHY_API_BASE_URL}/app/${appId}/package/list?limit=${PUSHY_PACKAGE_LIST_LIMIT}&offset=0`;
	const response = await fetch(url, {
		headers: {
			'x-api-token': PUSHY_API_TOKEN,
		},
	});

	if (!response.ok) {
		throw new Error(`Pushy 基准 APP 版本读取失败，状态码：${response.status}`);
	}

	const data = (await response.json()) as PushyPackageListResponse;
	return normalizeBaseVersions(Array.isArray(data.data) ? data.data : []);
}

function mergeBaseVersionsWithCurrent(
	baseVersions: string[],
	previousRows: EditablePlatformRow[],
) {
	const previousByVersion = new Map(
		previousRows.map((row) => [row.version, row] as const),
	);

	return baseVersions.map((version) => {
		const previous = previousByVersion.get(version);
		return {
			id: previous?.id || createRowId(),
			version,
			enabled: previous?.enabled ?? false,
		};
	});
}

type ShowHotUpdateBtnConfigPanelProps = {
	refreshKey?: number;
};

export default function ShowHotUpdateBtnConfigPanel({
	refreshKey = 0,
}: ShowHotUpdateBtnConfigPanelProps) {
	const [activeEnv, setActiveEnv] = useState<AppConfigEnv>('test');
	const [activePlatform, setActivePlatform] =
		useState<ShowHotUpdateBtnPlatform>('ios');
	const [envStates, setEnvStates] = useState<Record<AppConfigEnv, EnvState>>({
		test: createEmptyEnvState(),
		production: createEmptyEnvState(),
	});
	const [baseVersionStates, setBaseVersionStates] = useState(
		createEmptyBaseVersionStates,
	);

	const cosUploader = useMemo(
		() =>
			new BeLinkCOS({
				mode: 'production',
			}) as unknown as CosUploader,
		[],
	);

	const currentEnvConfig = useMemo(() => {
		return (
			APP_CONFIG_ENV_OPTIONS.find((item) => item.value === activeEnv) ||
			APP_CONFIG_ENV_OPTIONS[0]
		);
	}, [activeEnv]);

	const currentState = envStates[activeEnv];
	const hasChanges =
		serializeShowHotUpdateBtn(currentState.showHotUpdateBtn) !==
		currentState.lastSavedShowHotUpdateBtn;

	const updateEnvState = useCallback(
		(env: AppConfigEnv, updater: (state: EnvState) => EnvState) => {
			setEnvStates((prev) => ({
				...prev,
				[env]: updater(prev[env]),
			}));
		},
		[],
	);

	const updateBaseVersionState = useCallback(
		(
			env: AppConfigEnv,
			platform: ShowHotUpdateBtnPlatform,
			updater: (state: BaseVersionState) => BaseVersionState,
		) => {
			setBaseVersionStates((prev) => ({
				...prev,
				[env]: {
					...prev[env],
					[platform]: updater(prev[env][platform]),
				},
			}));
		},
		[],
	);

	const updateCurrentShowHotUpdateBtn = useCallback(
		(
			updater: (config: EditableShowHotUpdateBtn) => EditableShowHotUpdateBtn,
		) => {
			updateEnvState(activeEnv, (state) => ({
				...state,
				showHotUpdateBtn: updater(state.showHotUpdateBtn),
			}));
		},
		[activeEnv, updateEnvState],
	);

	const loadEnvConfig = useCallback(
		async (env: AppConfigEnv) => {
			const envConfig =
				APP_CONFIG_ENV_OPTIONS.find((item) => item.value === env) ||
				APP_CONFIG_ENV_OPTIONS[0];

			updateEnvState(env, (state) => ({ ...state, loading: true }));

			try {
				const response = await fetch(`${envConfig.url}?t=${Date.now()}`, {
					cache: 'no-store',
				});
				if (!response.ok) {
					throw new Error(`读取失败，状态码：${response.status}`);
				}

				const data = (await response.json()) as RemoteConfig;
				const showHotUpdateBtn = normalizeShowHotUpdateBtn(data);

				updateEnvState(env, (state) => ({
					...state,
					loading: false,
					loaded: true,
					lastModified: formatYmdHmsZh(
						response.headers.get('Last-Modified') || '',
					),
					fullConfig: data,
					showHotUpdateBtn,
					lastSavedShowHotUpdateBtn:
						serializeShowHotUpdateBtn(showHotUpdateBtn),
				}));
			} catch (error) {
				updateEnvState(env, (state) => ({ ...state, loading: false }));
				message.error(
					`读取${envConfig.label}显示强制更新按钮配置失败：${getErrorMessage(error)}`,
				);
			}
		},
		[updateEnvState],
	);

	const syncPushyBaseVersions = useCallback(
		async (env: AppConfigEnv, platform: ShowHotUpdateBtnPlatform) => {
			updateBaseVersionState(env, platform, (state) => ({
				...state,
				loading: true,
				error: '',
			}));

			try {
				const baseVersions = await fetchPushyBaseVersions(env, platform);
				updateEnvState(env, (state) => ({
					...state,
					showHotUpdateBtn: {
						...state.showHotUpdateBtn,
						[platform]: mergeBaseVersionsWithCurrent(
							baseVersions,
							state.showHotUpdateBtn[platform],
						),
					},
				}));
				updateBaseVersionState(env, platform, (state) => ({
					...state,
					loading: false,
					error: '',
				}));
				return { success: true as const, count: baseVersions.length };
			} catch (error) {
				const errorMessage = getErrorMessage(error);
				updateBaseVersionState(env, platform, (state) => ({
					...state,
					loading: false,
					error: errorMessage,
				}));
				return { success: false as const, error: errorMessage };
			}
		},
		[updateBaseVersionState, updateEnvState],
	);

	const syncAllPushyBaseVersions = useCallback(
		async (env: AppConfigEnv) => {
			const results = await Promise.all(
				PUSHY_PLATFORMS.map(async (platform) => ({
					platform,
					result: await syncPushyBaseVersions(env, platform),
				})),
			);

			const successItems = results.filter((item) => item.result.success);
			const failedItems = results.filter((item) => !item.result.success);

			if (successItems.length > 0) {
				const summary = successItems
					.map(
						(item) =>
							`${PLATFORM_META[item.platform].label} ${
								item.result.success ? item.result.count : 0
							} 条`,
					)
					.join('，');
				message.success(`三端同步完成：${summary}`);
			}

			if (failedItems.length > 0) {
				const summary = failedItems
					.map(
						(item) =>
							`${PLATFORM_META[item.platform].label}（${
								item.result.success ? '' : item.result.error
							}）`,
					)
					.join('，');
				message.error(`部分平台同步失败：${summary}`);
			}
		},
		[syncPushyBaseVersions],
	);

	useEffect(() => {
		if (!currentState.loaded && !currentState.loading) {
			void loadEnvConfig(activeEnv);
		}
	}, [activeEnv, currentState.loaded, currentState.loading, loadEnvConfig]);

	useEffect(() => {
		if (refreshKey > 0) {
			void loadEnvConfig(activeEnv);
		}
	}, [activeEnv, loadEnvConfig, refreshKey]);

	const handleSave = useCallback(async () => {
		const validationMessage = validateShowHotUpdateBtn(
			currentState.showHotUpdateBtn,
		);
		if (validationMessage) {
			message.error(validationMessage);
			return;
		}
		if (!currentState.fullConfig) {
			message.error('还没有读取到完整配置，无法保存');
			return;
		}

		updateEnvState(activeEnv, (state) => ({ ...state, saving: true }));

		try {
			const nextConfig: RemoteConfig = {
				...currentState.fullConfig,
				showHotUpdateBtn: buildShowHotUpdateBtnForSave(
					currentState.showHotUpdateBtn,
				),
			};

			await uploadTextToCosPath(cosUploader, {
				path: new URL(currentEnvConfig.url).pathname,
				body: formatConfigForUpload(nextConfig),
				contentType: 'application/json; charset=utf-8',
			});

			updateEnvState(activeEnv, (state) => ({
				...state,
				loading: false,
				saving: false,
				loaded: true,
				lastModified: formatYmdHmsZh(new Date()),
				fullConfig: nextConfig,
				lastSavedShowHotUpdateBtn: serializeShowHotUpdateBtn(
					currentState.showHotUpdateBtn,
				),
			}));
			message.success(`${currentEnvConfig.label}显示强制更新按钮配置保存成功`);
		} catch (error) {
			updateEnvState(activeEnv, (state) => ({ ...state, saving: false }));
			message.error(`保存失败：${getErrorMessage(error)}`);
		}
	}, [
		activeEnv,
		cosUploader,
		currentEnvConfig.label,
		currentEnvConfig.url,
		currentState.fullConfig,
		currentState.showHotUpdateBtn,
		updateEnvState,
	]);

	const renderPlatformCard = (platform: ShowHotUpdateBtnPlatform) => {
		const meta = PLATFORM_META[platform];
		const rows = currentState.showHotUpdateBtn[platform];
		const syncState = baseVersionStates[activeEnv][platform];

		const columns: ColumnsType<EditablePlatformRow> = [
			{
				title: '版本号',
				dataIndex: 'version',
				render: (value: string) =>
					value ? (
						<Tag color="purple" style={{ fontSize: 13, margin: 0 }}>
							{value}
						</Tag>
					) : (
						<Text type="secondary">-</Text>
					),
			},
			{
				title: '显示强制更新按钮',
				dataIndex: 'enabled',
				width: 180,
				render: (_value: boolean, row) => (
					<Switch
						checked={row.enabled}
						checkedChildren="显示"
						unCheckedChildren="隐藏"
						onChange={(value) =>
							updateCurrentShowHotUpdateBtn((config) => ({
								...config,
								[platform]: updateRow(config[platform], row.id, (item) => ({
									...item,
									enabled: value,
								})),
							}))
						}
					/>
				),
			},
		];

		return (
			<Card
				key={platform}
				size="small"
				styles={{ body: { padding: 16 } }}
				title={
					<Space size={8}>
						<Tag color={meta.color} style={{ margin: 0 }}>
							{meta.label}
						</Tag>
						<Text strong>平台配置</Text>
					</Space>
				}
			>
				<Space direction="vertical" size="middle" style={{ width: '100%' }}>
					{syncState.error ? (
						<Alert
							type="warning"
							showIcon
							message="Pushy 基准 APP 版本读取失败"
							description={syncState.error}
						/>
					) : null}

					<Table
						size="middle"
						rowKey="id"
						pagination={false}
						dataSource={rows}
						columns={columns}
						scroll={{ x: 'max-content' }}
						locale={{
							emptyText: (
								<Empty
									image={Empty.PRESENTED_IMAGE_SIMPLE}
									description="暂无配置，点击「新增版本（同步 Pushy 数据）」获取版本列表"
								/>
							),
						}}
					/>
				</Space>
			</Card>
		);
	};

	const renderEnvPanel = () => (
		<Spin spinning={currentState.loading} tip="读取配置中...">
			<Space
				direction="vertical"
				size="middle"
				style={{ width: '100%', paddingBottom: 88 }}
			>
				<Descriptions
					size="small"
					bordered
					column={{ xs: 1, sm: 1, md: 2 }}
					items={[
						{
							key: 'env',
							label: '当前环境',
							children: (
								<Tag color={currentEnvConfig.tagColor} style={{ margin: 0 }}>
									{currentEnvConfig.label}
								</Tag>
							),
						},
						{
							key: 'modified',
							label: '最近读取/保存',
							children: (
								<Text type="secondary">{currentState.lastModified || '-'}</Text>
							),
						},
						{
							key: 'url',
							label: '配置地址',
							span: 2,
							children: (
								<Paragraph
									copyable={{ text: currentEnvConfig.url }}
									style={{
										marginBottom: 0,
										fontFamily:
											'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
										fontSize: 12,
									}}
								>
									{currentEnvConfig.url}
								</Paragraph>
							),
						},
					]}
				/>

				<Tabs
					type="card"
					activeKey={activePlatform}
					onChange={(key) => setActivePlatform(key as ShowHotUpdateBtnPlatform)}
					tabBarExtraContent={
						<Button
							type="primary"
							icon={<SyncOutlined />}
							loading={PUSHY_PLATFORMS.some(
								(platform) => baseVersionStates[activeEnv][platform].loading,
							)}
							onClick={() => void syncAllPushyBaseVersions(activeEnv)}
						>
							同步三端 Pushy 数据
						</Button>
					}
					items={PUSHY_PLATFORMS.map((platform) => {
						const meta = PLATFORM_META[platform];
						return {
							key: platform,
							label: (
								<Text style={{ fontSize: 15 }} strong>
									{meta.label}
								</Text>
							),
							children: renderPlatformCard(platform),
						};
					})}
				/>

				<Affix offsetBottom={0}>
					<div
						style={{
							zIndex: 10,
							padding: '12px 20px',
							background: 'var(--ant-color-bg-container, #fff)',
							borderTop: '1px solid var(--ant-color-split, #f0f0f0)',
							boxShadow: '0 -6px 16px rgba(0,0,0,0.06)',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							gap: 16,
							flexWrap: 'wrap',
						}}
					>
						<Space size="small">
							{hasChanges ? (
								<Text type="warning" strong>
									⚠ 有未保存修改
								</Text>
							) : (
								<Text type="secondary">所有修改已保存</Text>
							)}
							{currentState.lastModified ? (
								<Text type="secondary">
									· 上次时间 {currentState.lastModified}
								</Text>
							) : null}
						</Space>
						<Space>
							<Button
								icon={<ReloadOutlined />}
								onClick={() => void loadEnvConfig(activeEnv)}
								loading={currentState.loading}
								disabled={currentState.saving}
							>
								重新读取
							</Button>
							<Button
								type="primary"
								icon={<SaveOutlined />}
								onClick={() => void handleSave()}
								loading={currentState.saving}
								disabled={currentState.loading || !currentState.loaded}
							>
								保存配置
							</Button>
						</Space>
					</div>
				</Affix>
			</Space>
		</Spin>
	);

	return (
		<Tabs
			activeKey={activeEnv}
			onChange={(key) => setActiveEnv(key as AppConfigEnv)}
			items={APP_CONFIG_ENV_OPTIONS.map((option) => ({
				key: option.value,
				label: <Text style={{ fontSize: 16 }}>{option.label}</Text>,
				children: renderEnvPanel(),
			}))}
		/>
	);
}
